/**
 * Timezone Utility - Centralized Date/Time Handling
 * 
 * ENTERPRISE SOLUTION: All timestamps in this application are:
 * - Stored in UTC on the backend
 * - Displayed in Indian Standard Time (IST - UTC+5:30) on the frontend
 * 
 * This module handles the conversion transparently, ensuring consistent
 * timezone handling across the entire application.
 * 
 * CRITICAL: All date formatting and relative time calculations MUST go through
 * this module to ensure consistent IST display.
 */

// IST timezone identifier
export const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * CRITICAL: Normalize any timestamp to ensure it's treated as UTC.
 * 
 * Backend sends timestamps in UTC but sometimes without timezone indicator.
 * JavaScript's Date parser treats timestamps without timezone as LOCAL time,
 * which causes the "5 hours ago" bug when user is in IST.
 * 
 * This function ensures all timestamps are correctly interpreted as UTC.
 * 
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {Date} Date object with correct UTC interpretation
 */
export const parseAsUTC = (dateInput) => {
  if (!dateInput) return null;
  
  // If already a Date object, return as-is
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }
  
  // If string, check if it has timezone info
  const dateStr = String(dateInput);
  
  // If the timestamp already has timezone info (Z, +00:00, +05:30, etc.), parse directly
  if (/[Zz]$/.test(dateStr) || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // If no timezone info, assume UTC and append 'Z'
  // This is the fix for the "5 hours ago" bug
  const date = new Date(dateStr + 'Z');
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Format a date/timestamp for display in IST.
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in IST
 */
export const formatIST = (dateInput, options = {}) => {
  if (!dateInput) return '—';
  
  const {
    includeTime = true,
    includeSeconds = true,
    includeDate = true,
    shortMonth = true,
  } = options;
  
  try {
    // CRITICAL: Use parseAsUTC to correctly interpret backend timestamps
    const date = parseAsUTC(dateInput);
    if (!date) return '—';
    
    const formatOptions = {
      timeZone: IST_TIMEZONE,
    };
    
    if (includeDate) {
      formatOptions.day = 'numeric';
      formatOptions.month = shortMonth ? 'short' : 'long';
      formatOptions.year = 'numeric';
    }
    
    if (includeTime) {
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
      if (includeSeconds) {
        formatOptions.second = '2-digit';
      }
      formatOptions.hour12 = false;
    }
    
    const formatted = date.toLocaleString('en-IN', formatOptions);
    return includeTime ? `${formatted} IST` : formatted;
  } catch (e) {
    console.error('Error formatting date:', e);
    return '—';
  }
};

/**
 * Format a timestamp as time only in IST.
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Time string like "15:30:45 IST"
 */
export const formatISTTime = (dateInput) => {
  return formatIST(dateInput, { includeDate: false });
};

/**
 * Format a timestamp as date only in IST.
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Date string like "23 Jan 2026"
 */
export const formatISTDate = (dateInput) => {
  return formatIST(dateInput, { includeTime: false });
};

/**
 * Format a timestamp as relative time (e.g., "5m ago", "2h ago").
 * 
 * CRITICAL: This function now correctly handles UTC timestamps from the backend.
 * The "5 hours ago" bug was caused by treating UTC timestamps as local time.
 * 
 * @param {string|Date} dateInput - ISO string or Date object (assumed UTC if no timezone)
 * @returns {string} Relative time string
 */
export const formatTimeAgoIST = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    // CRITICAL: Use parseAsUTC to correctly interpret backend timestamps
    const date = parseAsUTC(dateInput);
    if (!date) return '';
    
    const now = new Date();
    const diffMs = now - date;
    
    // Handle future dates gracefully
    if (diffMs < 0) return 'just now';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  } catch (e) {
    console.error('Error calculating time ago:', e);
    return '';
  }
};

/**
 * Get current date/time in IST as a formatted string.
 * @returns {string} Current IST timestamp
 */
export const nowIST = () => {
  return formatIST(new Date());
};

/**
 * Format timestamp for display in table cells or compact views.
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Compact formatted string like "23 Jan, 15:30 IST"
 */
export const formatISTCompact = (dateInput) => {
  if (!dateInput) return '—';
  
  try {
    // CRITICAL: Use parseAsUTC to correctly interpret backend timestamps
    const date = parseAsUTC(dateInput);
    if (!date) return '—';
    
    const formatted = date.toLocaleString('en-IN', {
      timeZone: IST_TIMEZONE,
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    return `${formatted} IST`;
  } catch (e) {
    return '—';
  }
};

/**
 * Format timestamp for history/timeline events.
 * Supports multiple output formats based on options.
 * 
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {Object} options - Formatting options
 * @param {boolean} options.showFull - If true, show full date and time
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (dateInput, options = {}) => {
  if (!dateInput) return '—';
  
  const { showFull = false } = options;
  
  try {
    // CRITICAL: Use parseAsUTC to correctly interpret backend timestamps
    const date = parseAsUTC(dateInput);
    if (!date) return '—';
    
    if (showFull) {
      return formatIST(date, { includeSeconds: false });
    }
    
    // For relative display, show relative time for recent, date for older
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    
    if (diffDays < 7) {
      return formatTimeAgoIST(date);
    }
    
    return formatISTCompact(date);
  } catch (e) {
    return '—';
  }
};

export default {
  formatIST,
  formatISTTime,
  formatISTDate,
  formatTimeAgoIST,
  formatISTCompact,
  formatTimestamp,
  nowIST,
  parseAsUTC,
  IST_TIMEZONE,
};
